import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, Edit2, Sliders, XCircle, X, Plus } from "react-feather";
import { toast } from "react-toastify";
import {
  disableAIPolicyAPI,
  getAIPoliciesAPI,
  updateAIPolicyScopeAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./PolicyList.module.scss";

// ─── Scope helpers ─────────────────────────────────────────────────────────────
function scopeClass(t) {
  if (t === "default") return styles.scopeDefault;
  if (t === "plan") return styles.scopePlan;
  return styles.scopeTenant;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function SkeletonGrid() {
  return (
    <div className={styles.skeletonGrid}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className={styles.skeletonCard} />
      ))}
    </div>
  );
}

function PolicyCard({ row, onAssignScope, onDisable }) {
  return (
    <div
      className={`${styles.card} ${row.status === "disabled" ? styles.disabled : ""}`}
    >
      {/* Top */}
      <div className={styles.cardTop}>
        <h3 className={styles.cardName}>{row.name}</h3>
        <div className={styles.cardMeta}>
          <span className={styles.badgeModule}>{row.module}</span>
          <span
            className={`${styles.badgeStatus} ${row.status === "active" ? styles.active : styles.disabled}`}
          >
            {row.status}
          </span>
          <span className={scopeClass(row.scopeType)}>
            {row.scopeLabel}
            {row.status === "disabled" && row.activeTenantRefs > 0 && (
              <AlertTriangle
                size={11}
                className={styles.warnIcon}
                aria-label="Active tenant references"
              />
            )}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className={styles.cardBody}>
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>Model route</span>
          <span className={styles.fieldValue}>{row.modelRoute}</span>
        </div>
        <div className={styles.fieldRow}>
          <span className={styles.fieldLabel}>Threshold</span>
          <span className={styles.fieldValue}>{row.threshold}</span>
        </div>
      </div>

      {/* Footer actions */}
      <div className={styles.cardFooter}>
        <Link
          className={styles.btnOutline}
          to={platformUrl(`ai-policies/${row.id}`)}
        >
          <Edit2 size={12} />
          Edit
        </Link>
        <button className={styles.btnGhost} onClick={() => onAssignScope(row)}>
          <Sliders size={12} />
          Assign scope
        </button>
        <button
          className={styles.btnDanger}
          onClick={() => onDisable(row.id)}
          disabled={row.status === "disabled"}
        >
          <XCircle size={12} />
          Disable
        </button>
      </div>
    </div>
  );
}

// ─── Scope Modal ───────────────────────────────────────────────────────────────
function ScopeModal({ row, onClose, onSave }) {
  const [form, setForm] = useState({
    type: row.scopeType,
    label: row.scopeLabel,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await onSave(row.id, form);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Assign scope</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.formGroup}>
            <label htmlFor="scope-type">Type</label>
            <select
              id="scope-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              <option value="default">Default</option>
              <option value="plan">Plan</option>
              <option value="tenant">Tenant</option>
            </select>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="scope-label">Label</label>
            <input
              id="scope-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────────
function ConfirmDisableModal({ onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  async function handleConfirm() {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2>Disable policy?</h2>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={14} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.confirmIcon}>
            <AlertTriangle size={24} />
          </div>
          <p className={styles.confirmMessage}>
            Downstream modules will fall back to the next matching policy. This
            action can be reversed by re-enabling the policy.
          </p>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.btnGhost} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.btnDanger}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? "Disabling…" : "Disable policy"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function PolicyList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scopeModal, setScopeModal] = useState(null); // row | null
  const [disableId, setDisableId] = useState(null); // id | null

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAIPoliciesAPI();
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ─── Stats ──────────────────────────────────────────────────────────────────
  const stats = useMemo(
    () => ({
      total: rows.length,
      active: rows.filter((r) => r.status === "active").length,
      disabled: rows.filter((r) => r.status === "disabled").length,
      scopes: [...new Set(rows.map((r) => r.scopeType))].length,
    }),
    [rows],
  );

  // ─── Handlers ───────────────────────────────────────────────────────────────
  async function handleScopeUpdate(id, form) {
    try {
      await updateAIPolicyScopeAPI(id, form);
      toast.success("Scope updated");
      setScopeModal(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function handleDisableConfirm() {
    try {
      await disableAIPolicyAPI(disableId);
      toast.success("Policy disabled");
      setDisableId(null);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  }

  // ─── Loading state ──────────────────────────────────────────────────────────
  if (loading && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="AI policies"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "AI policies" },
          ]}
        />
        <div className={styles.body}>
          <SkeletonGrid />
        </div>
      </div>
    );
  }

  // ─── Error state ─────────────────────────────────────────────────────────────
  if (error && !rows.length) {
    return (
      <div className={styles.page}>
        <PageHeader
          title="AI policies"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "AI policies" },
          ]}
        />
        <div className={styles.body}>
          <div className={styles.grid}>
            <div className={styles.emptyState}>
              <AlertTriangle size={40} />
              <h3>Could not load policies</h3>
              <p>{error.message}</p>
              <button className={styles.btnPrimary} onClick={load}>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main render ─────────────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      <PageHeader
        title="AI policies"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "AI policies" },
        ]}
        primaryAction={{
          label: "Create policy",
          onClick: () => toast.info("Create policy (simulated)"),
        }}
        secondaryActions={[
          {
            label: "Model routing",
            onClick: () => navigate(platformUrl("ai-policies/routing")),
          },
        ]}
      />

      <div className={styles.body}>
        {/* Stats */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Total policies</div>
            <div className={styles.statValue}>{stats.total}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Active</div>
            <div className={styles.statValue}>{stats.active}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Disabled</div>
            <div className={styles.statValue}>{stats.disabled}</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statLabel}>Scope types</div>
            <div className={styles.statValue}>{stats.scopes}</div>
          </div>
        </div>

        {/* Cards grid */}
        {rows.length === 0 ? (
          <div className={styles.grid}>
            <div className={styles.emptyState}>
              <AlertTriangle size={40} />
              <h3>No policies found</h3>
              <p>Create your first AI policy to get started.</p>
              <button
                className={styles.btnPrimary}
                // onClick={() => toast.info("Create policy (simulated)")}
              >
                <Plus size={14} />
                Create policy
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.grid}>
            {rows.map((row) => (
              <PolicyCard
                key={row.id}
                row={row}
                onAssignScope={(r) => setScopeModal(r)}
                onDisable={(id) => setDisableId(id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Scope modal */}
      {scopeModal && (
        <ScopeModal
          row={scopeModal}
          onClose={() => setScopeModal(null)}
          onSave={handleScopeUpdate}
        />
      )}

      {/* Confirm disable modal */}
      {disableId && (
        <ConfirmDisableModal
          onClose={() => setDisableId(null)}
          onConfirm={handleDisableConfirm}
        />
      )}
    </div>
  );
}
